export interface RenewalRecommendationInput {
  enabled: boolean;
  price?: number | null;
  billingInterval?: string | null;
  intervalMonths?: number | null;
  monthlyCreditsAllocated?: number | null;
  upfrontCredits?: number | null;
  label?: string;
}

export interface ForeverPlanRecommendationInput {
  planId: string;
  planName: string;
  creditsPerBundle: number;
  pricePerBundle: number;
}

export interface CreditTopUpRecommendationInput {
  shortfallCredits: number;
  renewal?: RenewalRecommendationInput | null;
  foreverPlans: ForeverPlanRecommendationInput[];
  maxBundleQuantity?: number;
}

export interface CreditTopUpRecommendationCandidate {
  kind: 'renewal' | 'forever';
  key: string;
  label: string;
  totalPrice: number;
  coveredCredits: number;
  overageCredits: number;
  quantity?: number;
  planId?: string;
  planName?: string;
}

export interface CreditTopUpRecommendationResult {
  recommended: CreditTopUpRecommendationCandidate | null;
  candidates: CreditTopUpRecommendationCandidate[];
}

const toNonNegativeNumber = (value: unknown): number => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return 0;
  return Math.max(0, numeric);
};

const resolveRenewalCoveredCredits = (renewal: RenewalRecommendationInput): number => {
  const intervalRaw = (renewal.billingInterval || '').toString().trim().toLowerCase();
  const intervalMonths = toNonNegativeNumber(renewal.intervalMonths);
  const resolvedIntervalMonths =
    intervalMonths > 0
      ? intervalMonths
      : (intervalRaw.startsWith('year') || intervalRaw.startsWith('annual') ? 12 : 1);
  const monthlyCredits = toNonNegativeNumber(renewal.monthlyCreditsAllocated);
  const upfrontCredits = toNonNegativeNumber(renewal.upfrontCredits);
  const annualLike =
    resolvedIntervalMonths >= 12 ||
    intervalRaw.startsWith('year') ||
    intervalRaw.startsWith('annual');
  if (annualLike) {
    if (upfrontCredits > 0) return upfrontCredits;
    return monthlyCredits * resolvedIntervalMonths;
  }
  return monthlyCredits;
};

const sortCandidates = (
  a: CreditTopUpRecommendationCandidate,
  b: CreditTopUpRecommendationCandidate
): number => {
  if (a.totalPrice !== b.totalPrice) return a.totalPrice - b.totalPrice;
  if (a.overageCredits !== b.overageCredits) return a.overageCredits - b.overageCredits;
  if (a.kind !== b.kind) return a.kind === 'renewal' ? -1 : 1;
  return a.label.localeCompare(b.label);
};

export const chooseCreditTopUpRecommendation = (
  input: CreditTopUpRecommendationInput
): CreditTopUpRecommendationResult => {
  const shortfallCredits = toNonNegativeNumber(input.shortfallCredits);
  if (shortfallCredits <= 0) {
    return { recommended: null, candidates: [] };
  }

  const maxBundleQuantity = Math.max(1, Math.floor(toNonNegativeNumber(input.maxBundleQuantity || 10)));
  const candidates: CreditTopUpRecommendationCandidate[] = [];

  const renewal = input.renewal;
  if (renewal && renewal.enabled) {
    const renewalPrice = toNonNegativeNumber(renewal.price);
    const renewalCoveredCredits = resolveRenewalCoveredCredits(renewal);
    if (renewalCoveredCredits >= shortfallCredits && renewalPrice >= 0) {
      candidates.push({
        kind: 'renewal',
        key: 'renewal',
        label: renewal.label || 'Renew Plan Now',
        totalPrice: renewalPrice,
        coveredCredits: renewalCoveredCredits,
        overageCredits: renewalCoveredCredits - shortfallCredits,
      });
    }
  }

  for (const plan of input.foreverPlans || []) {
    const creditsPerBundle = toNonNegativeNumber(plan.creditsPerBundle);
    const pricePerBundle = toNonNegativeNumber(plan.pricePerBundle);
    if (creditsPerBundle <= 0) continue;
    const quantity = Math.max(1, Math.ceil(shortfallCredits / creditsPerBundle));
    if (quantity > maxBundleQuantity) continue;
    const coveredCredits = quantity * creditsPerBundle;
    if (coveredCredits < shortfallCredits) continue;
    candidates.push({
      kind: 'forever',
      key: `forever:${plan.planId}`,
      label: `${quantity} ${quantity === 1 ? 'bundle' : 'bundles'} of ${plan.planName}`,
      totalPrice: pricePerBundle * quantity,
      coveredCredits,
      overageCredits: coveredCredits - shortfallCredits,
      quantity,
      planId: plan.planId,
      planName: plan.planName,
    });
  }

  candidates.sort(sortCandidates);
  return {
    recommended: candidates[0] || null,
    candidates,
  };
};

